<template>
    <div>
        <LoadingIntro v-if="loading && !singleProgram" />

        <div v-if="singleProgram && (!loading || isCompareFetching || isSaveFetching)" class="single-program grid-wrapper" :class="{ 'loading': loading }" id="content">
            <div class="full-width-grid intro-background margin--l-xl">
                <div class="grid-wrapper">
                    <div class="intro__content single-program__intro">
                        <div class="single-program__intro__left">
                            <button 
                                @click.prevent="goBackToPrograms" 
                                @keydown.enter.prevent="goBackToPrograms"
                                
                                class="secondary-button download__button red-color margin--2xs">
                                <span v-html="ico_arrow_left_16"/>
                                <p v-if="lastPath === 'programs'">{{ printString('back_to_search_results') }}</p>
                                <p v-else-if="lastPath === 'compare-programs'">{{ printString('back_to_compare') }}</p>
                                <p v-else-if="lastPath === 'my-dashboard'">{{ printString('back_to_my_dashboard') }}</p>
                            </button>

                            <h1 class="margin--xs black-primary" v-if="singleProgram.program_name">
                                {{ singleProgram.program_name }}
                            </h1>

                            <div class="single-program__intro__bottom">
                                <div class="single-program__intro__bottom__item" v-if="singleUniversity.name || singleProgram.faculty_name">
                                    <span v-html="ico_university"/>
                                    <p class="paragraph--teaser" v-if="singleUniversity.name">
                                        {{ singleUniversity.name }}
                                    </p>
                                    <p class="paragraph--teaser" v-if="singleProgram.faculty_name">
                                        {{ singleProgram.faculty_name }}
                                    </p>
                                </div>
                                <div class="single-program__intro__bottom__item" v-if="singleUniversity.province">
                                    <span v-html="ico_location"/>
                                    <p class="paragraph--teaser">
                                        {{ singleUniversity.province }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="single-program__intro__buttons">
                            <button class="general-button border-grey" @click="handleAddToCompare" 
                                :class="{ 'compared': isCompared, 'disabled': isCompareListFull && !isCompared }"
                                :disabled="isCompareFetching">
                                <span class="vh">{{ isCompared ? 'Remove from compare' : 'Add to compare' }}</span>
                                <span v-html="ico_compare_16"/>
                                {{ isCompareFetching ? printString('loading') : (isCompared ? printString('remove') : printString('compare')) }}
                            </button>
                            <Menu :theme="tooltipTheme" :container="menuContainer" :options="menuOptions" :disabled="is_logged_in">
                                <button 
                                    class="general-button border-grey" 
                                    :class="{ 'saved': isSaved, 'disabled': isSavedListFull && !isSaved }" 
                                    @click.stop="handleSaveClick"
                                    @keydown.enter.prevent="handleSaveClick"
                                    
                                    :disabled="isSaveFetching"
                                >
                                    <span class="vh">{{ isSaved ? 'Remove from favorites' : 'Add to favorites' }}</span>
                                    <span v-if="isSaved" v-html="ico_favorite_16_checked"/>
                                    <span v-else v-html="ico_favorite_16"/>
                                    {{ isSaveFetching ? printString('loading') : saveButtonText }}
                                </button>

                                <template #popper>
                                    <div v-if="!is_logged_in">
                                        <a class="secondary-button red-color" :href="loginUrl" @click.stop>
                                            {{ printString('sign_in') }}
                                        </a>
                                        {{ printString('to_add_to_list') }}
                                    </div>
                                    <div v-else>
                                        {{ saveButtonText }}
                                    </div>
                                </template>
                            </Menu>
                        </div>
                    </div>
                </div>

                <div class="intro-gradient">
                    <div class="intro-gradient__red"></div>
                    <div class="intro-gradient__yellow"></div>
                    <div class="intro-gradient__white"></div>
                    <div class="intro-gradient__white"></div>
                </div>
            </div>

            <div class="loader"></div>

            <div v-if="singleProgram && (!loading || isCompareFetching || isSaveFetching)" class="single-program__content__holder margin--xl-2xl">

                <div class="single-program__sidebar">
                    <div v-if="singleProgram.program_level_description" class="single-program__sidebar__item">
                        <h3 class="step--1">
                            {{ printString('program_level') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ singleProgram.program_level_description }}
                        </p>
                    </div>

                    <div v-if="singleProgram.program_language !== null" class="single-program__sidebar__item">
                        <h3 class="step--1">
                            {{ printString('language') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ singleProgram.program_language === 'fr' ? printString('french') : printString('english') }}
                        </p>
                    </div>

                    <div v-if="singleProgram.coop_available !== null" class="single-program__sidebar__item">
                        <h3 class="step--1">
                            {{ printString('co_op_availability') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ singleProgram.coop_available === '1' ? printString('yes') : printString('no') }}
                        </p>
                    </div>

                    <div v-if="singleProgram.remote_learning !== null" class="single-program__sidebar__item">
                        <h3 class="step--1">
                            {{ printString('remote_learning') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ singleProgram.remote_learning === '1' ? printString('yes') : printString('no') }}
                        </p>
                    </div>

                    <div v-if="singleProgram.area_of_study" class="single-program__sidebar__item">
                        <h3 class="step--1">
                            {{ printString('area_of_study') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ singleProgram.area_of_study }}
                        </p>
                    </div>

                    <a class="general-button border-red" v-if="singleProgram.program_url" :href="singleProgram.program_url" target="_blank">
                        {{ printString('program_website') }}
                        <span v-html="ico_external_16"/>
                    </a>                
                </div>

                <div class="single-program__content">
                    <div class="single-program__content__item" v-if="singleProgram.application_fee !== '0.00' && singleProgram.application_fee !== '0' && singleProgram.application_fee !== null && singleProgram.application_fee !== ''">
                        <h2 class="step--3">
                            {{ printString('application_fee') }}
                        </h2>
                        <p>
                            {{ singleProgram.application_fee }}
                        </p>
                    </div>
                    <div class="single-program__content__item" v-if="singleProgram.program_description">
                        <h2 class="step--3">
                            {{ printString('description') }}
                        </h2>
                        <div class="content paragraph--regular" v-html="singleProgram.program_description"/>
                    </div>
                    <div class="single-program__content__item" v-if="singleProgram.length_of_study && singleProgram.length_of_study !== '0' && singleProgram.length_of_study !== null">
                        <h2 class="step--3">
                            {{ printString('length_of_study') }}
                        </h2>
                        <p>
                            {{ singleProgram.length_of_study }}
                        </p>
                    </div>
                    <div class="single-program__content__item" v-if="singleProgram.admission_requirements">
                        <h2 class="step--3">
                            {{ printString('admission_requirements') }}
                        </h2>
                        <p>
                            {{ singleProgram.admission_requirements }}
                        </p>
                    </div>
                    <div class="single-program__content__item">
                        <p class="paragraph--teaser color--grey">
                            {{ printString('disclaimer_scholarship_single') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="singleUniversity" class="single-university grid-wrapper" :class="{ 'loading': loading }">
            <div class="full-width-grid background-grey">
                <div class="grid-wrapper">
                    <div class="single-university__top margin--l-xl">

                        <p class="paragraph--teaser">
                            {{ printString('school_details') }}
                        </p>

                        <h2 v-if="singleUniversity.name">
                            {{ singleUniversity.name }}
                        </h2>

                        <div class="single-program__intro__bottom__item" v-if="singleUniversity.city || singleUniversity.province">
                            <span v-html="ico_location"/>
                            <p class="paragraph--teaser">
                                {{ singleUniversity.city ? singleUniversity.city + ', ' : '' }}{{ singleUniversity.province }}
                            </p>
                        </div>

                    </div>
                </div>

                <SchoolContent v-if="singleUniversity" :university="singleUniversity" />
            </div>
        </div>

        <ComparePopup
            :compare-list="comparePrograms"
            :item-type="'program'"
            :is-visible="isComparePopupVisible"
            :is-loading="loading"
            @remove-from-compare="removeFromCompare"
            @hide-popup="hideComparePopup"
        />
    </div>
</template>

<script>
import { useRoute, useRouter } from 'vue-router';
import { Menu } from 'floating-vue'
import ComparePopup from './ComparePopup.vue';
import LoadingIntro from './LoadingIntro.vue';
import SchoolContent from './SchoolContent.vue';

export default {
    name: "SingleProgram",
    components: {
        Menu,
        ComparePopup,
        LoadingIntro,
        SchoolContent
    },
    data() {
        return {
            singleProgram: null,
            singleUniversity: null,
            loading: true,
            error: null,
            compareList: [],
            savedList: [],
            ico_compare_16: window.ico_compare_16,
            ico_favorite_16: window.ico_favorite_16,
            ico_favorite_16_checked: window.ico_favorite_16_checked,
            ico_arrow_left_16: window.ico_arrow_left_16,
            ico_location: window.ico_location,
            ico_external_16: window.ico_external_16,
            ico_university: window.ico_university,
            comparePrograms: [],
            isComparePopupVisible: false,
            maxCompareItems: 3,
            maxSavedItems: 10,
            site_lang: window.lang,
            is_logged_in: window.is_logged_in,
            saveButtonText: this.printString('save'),
            loginUrl: window.log_in_url,
            tooltipTheme: 'tooltips',
            menuContainer: null,
            menuOptions: {
                triggers: ['hover', 'focus', 'touch'],
                hideTriggers: ['hover', 'touch'],
                popperTriggers: ['hover', 'focus', 'focusin'],
            },
            isCompareFetching: false,
            isSaveFetching: false,
            previousRoute: null,
            lastPath: 'programs',
        };
    },
    computed: {
        isSaved() {
            console.log('isSaved', this.savedList, this.singleProgram?.id);
            return this.savedList.includes(this.singleProgram?.id);
        },
        isCompared() {
            return this.compareList.includes(this.singleProgram?.id);
        },
        isCompareListFull() {
            return this.compareList.length >= this.maxCompareItems;
        },
        isSavedListFull() {
            return this.savedList.length >= this.maxSavedItems;
        },
    },
    created() {
        
        this.route = useRoute();
        this.router = useRouter();
        
        // Get the initial previous route from the navigation state if available
        if (this.router.options.history.state?.back) {
            this.previousRoute = this.router.options.history.state.back;
            // Set lastPath based on the previous route
            if (this.previousRoute.includes('/programs')) {
                this.lastPath = 'programs';
            } else if (this.previousRoute.includes('/compare-programs')) {
                this.lastPath = 'compare-programs';
            } else if (this.previousRoute.includes('/my-dashboard')) {
                this.lastPath = 'my-dashboard';
            }
        }
    },
    mounted() {
        console.log('SingleProgram component mounted');
        console.log('Route params:', this.route.params);
        this.initializeProgram();
        this.setMenuContainer();

        this.$watch(
            () => this.singleProgram,
            (newProgram) => {
                if (newProgram) {
                    this.$nextTick(() => {
                        this.updateLanguageSwitcherUrl();
                    });
                }
            },
            { immediate: true }
        );
    },
    methods: {
        initializeProgram() {
            console.log('Initializing program');
            const apikey = window.api_key;
            const site_lang = this.site_lang;
            const programId = this.route.params.id;
            console.log('Fetching program with ID:', programId);
            this.fetchProgram(apikey, site_lang, programId);
            this.loadCompareListFromStorage();
            if (window.is_logged_in) {
                this.fetchSavedPrograms();
            }
        },
        goBackToPrograms() {
            // Use the stored previous route if it exists and is a valid programs page
            if (this.previousRoute && this.previousRoute.includes('/programs')) {
                // const route = this.site_lang === 'fr' ? '/fr/programs' : '/programs';
                const route = this.site_lang === 'fr' ? '/programs' : '/programs';
                this.router.push({ path: route });
            } else if(this.previousRoute && this.previousRoute.includes('/compare-programs')) {
                // const route = this.site_lang === 'fr' ? '/fr/compare-programs' : '/compare-programs';
                const route = this.site_lang === 'fr' ? '/compare-programs' : '/compare-programs';
                this.router.push({ path: route });
            } else if(this.previousRoute && this.previousRoute.includes('/my-dashboard')) {
                // const route = this.site_lang === 'fr' ? '/fr/my-dashboard' : '/my-dashboard';
                const route = this.site_lang === 'fr' ? '/my-dashboard' : '/my-dashboard';
                this.router.push({ path: route });
            } else {
                // Fallback to default behavior
                // const route = this.site_lang === 'fr' ? '/fr/programs' : '/programs';
                const route = this.site_lang === 'fr' ? '/programs' : '/programs';
                this.router.push({ path: route });
            }
        },
        async fetchProgram(apikey, site_lang, id) {
            console.log('Fetching program data', { apikey, site_lang, id });
            this.loading = true;
            this.error = null;
            
            try {
                const requestData = {
                    apikey: apikey,
                    site_lang: site_lang,
                    program_id: id,
                };

                // Try to get cached data first
                const cacheKey = await this.getCacheKey(requestData);
                const cachedData = await this.getCachedData(cacheKey);

                if (cachedData) {
                    console.log('Using cached program data');
                    this.handleProgramData(cachedData);
                    return;
                }

                const response = await fetch(`${window.api_base_url}/api/programs-single/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestData),
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.statusText}`);
                }

                const data = await response.json();
                
                // Cache the response
                await this.setCachedData(cacheKey, data);
                
                // Handle the data
                this.handleProgramData(data);

            } catch (err) {
                this.error = "Failed to load program. Please try again.";
                console.error("Error fetching program:", err);
            } finally {
                this.loading = false;
            }
        },
        async fetchSavedPrograms() {
            try {
                const response = await fetch(`${window.rest_api}/wp-json/custom/v1/get-saved-programs`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.nonce,
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const savedPrograms = await response.json();
                this.savedList = Array.isArray(savedPrograms) ? savedPrograms : [];
            } catch (error) {
                console.error("Error fetching saved programs:", error);
            }
        },

        async handleAddToCompare() {
            this.isCompareFetching = true;
            try {
                const programId = this.singleProgram.id;
                
                const index = this.compareList.indexOf(programId);
                
                if (index !== -1) {
                    this.compareList.splice(index, 1);
                    console.log(`Program removed from compare list: ${programId}`);
                } else if (this.compareList.length < this.maxCompareItems) {
                    this.compareList.push(programId);
                    console.log(`Program added to compare list: ${programId}`);
                    this.isComparePopupVisible = true;
                } else {
                    console.warn(`Compare list is full. Max ${this.maxCompareItems} programs allowed.`);
                }

                this.saveCompareListToStorage();
                await this.fetchComparePrograms();
            } catch (error) {
                console.error("Error in compare operation:", error);
            } finally {
                this.isCompareFetching = false;
            }
        },

        handleSaveClick(event) {
            event.stopPropagation();
            if (!this.is_logged_in) {
                window.location.href = this.loginUrl;
                return;
            } else {
                this.handleSaveToList();
            }
        },

        async handleSaveToList() {
            this.isSaveFetching = true;
            try {
                if (this.singleProgram) {
                    const programId = this.singleProgram.id;
                    
                    const index = this.savedList.indexOf(programId);

                    if (index !== -1) {
                        this.savedList.splice(index, 1);
                        this.saveButtonText = this.printString('save');
                        console.log(`Program removed from saved list: ${programId}`);
                    } else if (this.savedList.length < this.maxSavedItems) {
                        this.savedList.push(programId);
                        this.saveButtonText = this.printString('remove');
                        console.log(`Program saved: ${programId}`);
                    } else {
                        console.warn(`Saved list is full. Max ${this.maxSavedItems} programs allowed.`);
                        return;
                    }

                    console.log('Updated Saved list:', this.savedList);
                    await this.saveSavedListToBackend();
                }
            } catch (error) {
                console.error("Error in save operation:", error);
            } finally {
                this.isSaveFetching = false;
            }
        },

        loadCompareListFromStorage() {
            const storedList = localStorage.getItem('compareListPrograms');
            if (storedList) {
                this.compareList = JSON.parse(storedList);
                this.fetchComparePrograms();
            }
        },

        saveCompareListToStorage() {
            localStorage.setItem('compareListPrograms', JSON.stringify(this.compareList));
            console.log('Compare list saved to local storage:', this.compareList);
        },

        async fetchComparePrograms() {
            if (this.compareList.length < 1) {
                console.log('No programs to compare.');
                this.comparePrograms = [];
                this.isComparePopupVisible = false;
                this.isCompareFetching = false;
                return;
            }

            this.loading = true;
            this.isCompareFetching = true;
            
            try {
                const requestData = {
                    apikey: window.api_key,
                    program_ids: this.compareList,
                    site_lang: window.lang,
                };

                // Try to get cached compare data
                const cacheKey = await this.getCacheKey(`compare-${JSON.stringify(requestData)}`);
                const cachedData = await this.getCachedData(cacheKey);

                if (cachedData) {
                    console.log('Using cached compare data');
                    this.handleCompareData(cachedData);
                    return;
                }

                const response = await fetch(
                    `${window.api_base_url}/api/programs-by-id/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(requestData),
                    }
                );

                if (!response.ok) {
                    throw new Error(`Error: ${response.statusText}`);
                }

                const data = await response.json();
                
                // Cache the compare response with a shorter duration
                await this.setCachedData(cacheKey, data, 300); // 30 minutes cache for compare data
                
                // Handle the compare data
                this.handleCompareData(data);

            } catch (error) {
                console.error("Error fetching compare programs:", error);
            } finally {
                this.loading = false;
                this.isCompareFetching = false;
            }
        },

        removeFromCompare(programId) {
            this.isCompareFetching = true;
            const index = this.compareList.indexOf(programId);

            console.log('removeFromCompare', index);
            if (index !== -1) {
                this.compareList.splice(index, 1);
                this.saveCompareListToStorage();
                this.fetchComparePrograms();
            }
        },

        hideComparePopup() {
            this.isComparePopupVisible = false;
        },

        async saveSavedListToBackend() {
            this.loading = true;
            try {
                const response = await fetch(`${window.rest_api}/wp-json/custom/v1/save-programs`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.nonce,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ savedList: this.savedList }),
                });

                if (!response.ok) {
                    throw new Error(`Error saving programs: ${response.statusText}`);
                }

                console.log('Programs saved to ACF successfully.');
            } catch (error) {
                console.error('Error sending saved list to backend:', error);
            } finally {
                this.loading = false;
            }
        },

        updateLanguageSwitcherUrl() {
            if (this.singleProgram) {
                const languageSwitcher = document.querySelector('.languages');
                if (languageSwitcher) {
                    const langLink = languageSwitcher.querySelector(`.languages__item:not(.active)`);
                    
                    if (langLink) {
                        const programId = this.singleProgram.id;
                        let baseUrl = window.language_switcher_url;
                        let newPath;

                        if (this.site_lang === 'fr') {
                            // We're on French site, building English URL
                            newPath = `/program/${programId}`;
                            // Remove '/fr' from the end of baseUrl if it exists
                            baseUrl = baseUrl.replace(/\/fr\//, '');
                        } else {
                            // We're on English site, building French URL
                            newPath = `/program/${programId}`;
                        }
                        
                        const newUrl = `${baseUrl}${newPath}`;
                        langLink.href = newUrl;

                        console.log(`Language switcher URL updated: ${newUrl}`);
                    }
                }
            }
        },

        setMenuContainer() {
            this.$nextTick(() => {
                this.menuContainer = this.$el.querySelector('.single-program__intro__buttons');
            });
        },

        printString(string) {
            return window.translations[string];
        },

        // Add cache utility methods
        async getCacheKey(requestData) {
            const sortedData = Object.keys(requestData).sort().reduce((obj, key) => {
                obj[key] = requestData[key];
                return obj;
            }, {});
            return `program-single-${JSON.stringify(sortedData)}`;
        },

        async getCachedData(cacheKey) {
            try {
                const cache = await caches.open('program-single-cache');
                const cachedResponse = await cache.match(cacheKey);
                
                if (cachedResponse) {
                    const cacheData = await cachedResponse.json();
                    const ageInMinutes = (Date.now() - cacheData.timestamp) / (1000 * 60);
                    
                    if (ageInMinutes > cacheData.expirationMinutes) {
                        console.log('Cache expired, removing old data');
                        await cache.delete(cacheKey);
                        return null;
                    }
                    
                    return cacheData.data;
                }
            } catch (error) {
                console.warn('Error reading from cache:', error);
            }
            return null;
        },

        async setCachedData(cacheKey, data, expirationMinutes = 60) { // Longer cache for single program
            try {
                const cache = await caches.open('program-single-cache');
                const cacheData = {
                    data: data,
                    timestamp: Date.now(),
                    expirationMinutes: expirationMinutes
                };
                const response = new Response(JSON.stringify(cacheData));
                await cache.put(cacheKey, response);
            } catch (error) {
                console.warn('Error writing to cache:', error);
            }
        },

        // New method to handle program data consistently
        handleProgramData(data) {
            this.singleProgram = data.program[0];
            this.singleUniversity = data.university[0];
            console.log('Program data:', this.singleProgram);
            console.log('University data:', this.singleUniversity);
        },

        // New method to handle compare data consistently
        handleCompareData(data) {
            this.comparePrograms = data.results || [];
            this.isComparePopupVisible = this.comparePrograms.length > 0;
        },

        // Add utility method to manually clear cache if needed
        async clearProgramCache() {
            try {
                const cache = await caches.open('program-single-cache');
                await cache.keys().then(keys => {
                    keys.forEach(key => {
                        cache.delete(key);
                    });
                });
                console.log('Program cache cleared');
            } catch (error) {
                console.warn('Error clearing cache:', error);
            }
        },

        // formatDescription(text) {
        //     if (!text) return '';
            
        //     // Regular expression to match URLs
        //     const urlRegex = /(https?:\/\/[^\s]+)/g;
            
        //     // Replace URLs with anchor tags
        //     return text.replace(urlRegex, url => {
        //         return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        //     });
        // },
    },
    watch: {
        '$route.params.id': function(newId) {
            console.log('Route param id changed:', newId);
            this.initializeProgram();
            this.$nextTick(() => {
                this.setMenuContainer();
            });
        },
        singleProgram() {
            this.$nextTick(() => {
                this.setMenuContainer();
            });
        },
    }
};
</script>







